import { motion } from 'framer-motion';
import { Instagram, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.png';

const socialLinks = [
  { id: 'instagram', icon: Instagram, href: 'https://instagram.com/m1lkcode', label: 'Instagram' },
  { id: 'whatsapp', icon: MessageCircle, href: 'https://wa.me/5585985655143', label: 'WhatsApp' },
];

const footerLinks = [
  { label: 'Início', href: '#inicio' },
  { label: 'Serviços', href: '#servicos' },
  { label: 'Sobre', href: '#sobre' },
];

const legalLinks = [
  { label: 'Política de Uso', href: '/politica-de-uso' },
  { label: 'Política de Privacidade', href: '/politica-de-privacidade' },
  { label: 'Exclusão de Dados', href: '/exclusao-de-dados' },
];

export const Footer = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer
      data-testid="footer"
      className="py-10 md:py-12 border-t border-brand-grey/20 relative"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6">
          {/* Logo & Description */}
          <div className="md:col-span-5">
            <motion.a
              href="#inicio"
              data-testid="footer-logo-link"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              onClick={(e) => { e.preventDefault(); scrollToSection('#inicio'); }}
              className="inline-block mb-4"
            >
              <img
                src={logo}
                alt="M1LK CODE"
                className="h-7 w-auto"
                data-testid="footer-logo"
              />
            </motion.a>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              data-testid="footer-description"
              className="font-body text-sm text-brand-light/60 leading-relaxed max-w-sm"
            >
              Transformando ideias em soluções digitais.
              Landing pages, sites, e-commerces e automações
              para impulsionar seu negócio.
            </motion.p>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-3">
            <motion.h4
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="font-display text-sm uppercase tracking-[0.15em] text-brand-light mb-4"
            >
              Links Rápidos
            </motion.h4>
            <nav className="space-y-2">
              {footerLinks.map((link, index) => (
                <motion.a
                  key={link.label}
                  href={link.href}
                  data-testid={`footer-link-${link.label.toLowerCase()}`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                  className="block footer-link font-body text-sm"
                >
                  {link.label}
                </motion.a>
              ))}
            </nav>
          </div>

          {/* Social Links */}
          <div className="md:col-span-2">
            <motion.h4
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="font-display text-sm uppercase tracking-[0.15em] text-brand-light mb-4"
            >
              Redes Sociais
            </motion.h4>
            <div className="flex gap-4">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={social.id}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-link-${social.id}`}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="social-icon"
                    aria-label={social.label}
                  >
                    <Icon size={20} strokeWidth={1.5} />
                  </motion.a>
                );
              })}
            </div>
          </div>

          {/* Legal Links */}
          <div className="md:col-span-2">
            <motion.h4
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="font-display text-sm uppercase tracking-[0.15em] text-brand-light mb-4"
            >
              Políticas
            </motion.h4>
            <nav className="space-y-2">
              {legalLinks.map((link, index) => (
                <motion.div
                  key={link.label}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={link.href}
                    className="block footer-link font-body text-sm"
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-8 pt-5 flex flex-col items-center justify-center gap-4 text-center"
        >
          <p
            data-testid="copyright"
            className="font-body text-sm text-brand-grey"
          >
            &copy; {currentYear} M1LK CODE. Todos os direitos reservados.
          </p>
        </motion.div>
      </div>
    </footer>
  );
};
